import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { format, subDays } from 'date-fns';
import {
  AlertTriangle, Clock, LogOut, PhoneOff, Shield, TrendingDown,
  ChevronDown, ChevronUp, Plus, Edit3, X, Check, Search, Filter,
  User, AlertCircle, Award, Info, Mail, ChevronRight, FileText, Upload
} from 'lucide-react';
import {
  THRESHOLDS, CALLOUT_POINTS, POINT_TYPES,
  getActivePoints, calculateTotalPoints, getWarningLevel,
  getNextThreshold, getEmployeeSummary, formatPoints,
  calculateTardinessPoints, calculateEarlyLeavePoints,
  autoDetectPoints
} from '../utils/attendancePoints';
import { openWriteUpForPrint } from '../utils/writeUpPdf';
import { uploadJobPhoto } from '../utils/photoUpload';
import './AttendancePoints.css';

// ===== MAIN PAGE =====
function AttendancePoints() {
  const {
    state, getEmployeeAttendancePoints, addAttendancePoint,
    updateAttendancePoint, voidAttendancePoint,
    getEmployeeAvgWeeklyHours, getEmploymentStatus,
    getEmployeeShifts
  } = useApp();

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showScanModal, setShowScanModal] = useState(false);

  const activeEmployees = useMemo(() =>
    state.employees.filter(e => e.status === 'active'),
    [state.employees]
  );

  // Build summary for each employee
  const employeeSummaries = useMemo(() => {
    return activeEmployees.map(emp => {
      const points = getEmployeeAttendancePoints(emp.id);
      const summary = getEmployeeSummary(points);
      const employment = getEmploymentStatus(emp.id);
      return { ...emp, ...summary, employment };
    }).sort((a, b) => b.totalPoints - a.totalPoints);
  }, [activeEmployees, state.attendancePoints, state.jobs]);

  const filteredEmployees = useMemo(() => {
    let list = employeeSummaries;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(e =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q)
      );
    }
    if (filterStatus === 'warnings') {
      list = list.filter(e => e.totalPoints >= 3);
    } else if (filterStatus === 'good') {
      list = list.filter(e => e.totalPoints < 3);
    } else if (filterStatus === 'part_time') {
      list = list.filter(e => !e.employment.ptoEligible);
    }
    return list;
  }, [employeeSummaries, searchTerm, filterStatus]);

  // Scan all time records vs shifts to find unlogged infractions
  const handleScanForPoints = () => {
    setShowScanModal(true);
  };

  const statusColor = (status) => {
    if (status === 'critical') return '#991b1b';
    if (status === 'warning') return '#ef4444';
    if (status === 'caution') return '#f59e0b';
    return '#10b981';
  };

  const statusBg = (status) => {
    if (status === 'critical') return '#fef2f2';
    if (status === 'warning') return '#fef2f2';
    if (status === 'caution') return '#fffbeb';
    return '#f0fdf4';
  };

  return (
    <div className="attendance-page">
      {/* Header */}
      <div className="attendance-header">
        <div>
          <h1>Attendance Points</h1>
          <p className="attendance-subtitle">Track attendance, points, warnings, and PTO eligibility</p>
        </div>
        <div className="attendance-header-actions">
          <button className="btn-scan" onClick={handleScanForPoints}>
            <Search size={16} /> Scan Time Records
          </button>
        </div>
      </div>

      {/* Threshold Reference Bar */}
      <div className="threshold-bar">
        {THRESHOLDS.map(t => (
          <div key={t.level} className="threshold-item" style={{ borderColor: t.color }}>
            <span className="threshold-pts">{t.points} pts</span>
            <span className="threshold-label">{t.label}</span>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="attendance-stats">
        <div className="att-stat-card">
          <span className="att-stat-num">{employeeSummaries.filter(e => e.status === 'good').length}</span>
          <span className="att-stat-label">Good Standing</span>
        </div>
        <div className="att-stat-card caution">
          <span className="att-stat-num">{employeeSummaries.filter(e => e.status === 'caution').length}</span>
          <span className="att-stat-label">Caution (3+ pts)</span>
        </div>
        <div className="att-stat-card warning">
          <span className="att-stat-num">{employeeSummaries.filter(e => e.status === 'warning' || e.status === 'critical').length}</span>
          <span className="att-stat-label">Warning/Critical</span>
        </div>
        <div className="att-stat-card info">
          <span className="att-stat-num">{employeeSummaries.filter(e => !e.employment.ptoEligible).length}</span>
          <span className="att-stat-label">Part-Time (No PTO)</span>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="attendance-controls">
        <div className="att-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="att-filters">
          {['all', 'warnings', 'good', 'part_time'].map(f => (
            <button
              key={f}
              className={`att-filter-btn ${filterStatus === f ? 'active' : ''}`}
              onClick={() => setFilterStatus(f)}
            >
              {f === 'all' ? 'All' : f === 'warnings' ? 'Warnings' : f === 'good' ? 'Good Standing' : 'Part-Time'}
            </button>
          ))}
        </div>
      </div>

      {/* Employee List */}
      <div className="attendance-grid">
        {filteredEmployees.map(emp => (
          <EmployeePointCard
            key={emp.id}
            employee={emp}
            isSelected={selectedEmployee === emp.id}
            onSelect={() => setSelectedEmployee(selectedEmployee === emp.id ? null : emp.id)}
            onAddPoint={() => { setSelectedEmployee(emp.id); setShowAddModal(true); }}
            points={getEmployeeAttendancePoints(emp.id)}
            onEditPoint={(p) => setShowEditModal(p)}
            onVoidPoint={(id, reason) => voidAttendancePoint(id, reason)}
            statusColor={statusColor}
            statusBg={statusBg}
          />
        ))}
        {filteredEmployees.length === 0 && (
          <div className="att-empty">No employees match your filters.</div>
        )}
      </div>

      {/* Add Point Modal */}
      {showAddModal && selectedEmployee && (
        <AddPointModal
          employeeId={selectedEmployee}
          employees={activeEmployees}
          onClose={() => setShowAddModal(false)}
          onSave={async (data) => {
            await addAttendancePoint(data);
            setShowAddModal(false);
          }}
        />
      )}

      {/* Edit Point Modal */}
      {showEditModal && (
        <EditPointModal
          point={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSave={async (id, updates) => {
            await updateAttendancePoint(id, updates);
            setShowEditModal(null);
          }}
          onVoid={async (id, reason) => {
            await voidAttendancePoint(id, reason);
            setShowEditModal(null);
          }}
        />
      )}

      {/* Scan Modal */}
      {showScanModal && (
        <ScanModal
          employees={activeEmployees}
          state={state}
          getEmployeeShifts={getEmployeeShifts}
          existingPoints={state.attendancePoints}
          onClose={() => setShowScanModal(false)}
          onAddPoints={async (points) => {
            for (const p of points) {
              await addAttendancePoint(p);
            }
            setShowScanModal(false);
          }}
        />
      )}
    </div>
  );
}

// ===== EMPLOYEE POINT CARD =====
function EmployeePointCard({ employee, isSelected, onSelect, onAddPoint, points, onEditPoint, onVoidPoint, statusColor, statusBg }) {
  const activePoints = getActivePoints(points);
  const totalPoints = calculateTotalPoints(points);
  const warningLevel = getWarningLevel(totalPoints);
  const nextThreshold = getNextThreshold(totalPoints);

  const typeIcon = (type) => {
    switch (type) {
      case 'tardiness': return <Clock size={14} />;
      case 'early_leave': return <LogOut size={14} />;
      case 'callout': return <PhoneOff size={14} />;
      case 'no_call_no_show': return <AlertTriangle size={14} />;
      case 'reduction': return <TrendingDown size={14} />;
      case 'manager_override': return <Shield size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  return (
    <div className={`emp-point-card ${isSelected ? 'expanded' : ''}`} style={{ borderLeftColor: statusColor(employee.status) }}>
      <div className="emp-point-header" onClick={onSelect}>
        <div className="emp-point-info">
          <div className="emp-point-avatar" style={{ background: statusBg(employee.status), color: statusColor(employee.status) }}>
            {employee.firstName?.[0]}{employee.lastName?.[0]}
          </div>
          <div>
            <div className="emp-point-name">{employee.firstName} {employee.lastName}</div>
            <div className="emp-point-meta">
              <span className={`emp-status-badge ${employee.employment.status}`}>
                {employee.employment.status === 'full_time' ? 'Full-Time' : 'Part-Time'}
              </span>
              <span className="emp-avg-hrs">{employee.employment.avgWeeklyHours} hrs/wk avg</span>
              {!employee.employment.ptoEligible && (
                <span className="emp-no-pto">No PTO</span>
              )}
            </div>
          </div>
        </div>
        <div className="emp-point-right">
          <div className="emp-point-total" style={{ color: statusColor(employee.status) }}>
            {totalPoints.toFixed(2)} pts
          </div>
          {warningLevel && (
            <div className="emp-warning-badge" style={{ background: warningLevel.bgColor, color: warningLevel.color }}>
              {warningLevel.label}
            </div>
          )}
          {!warningLevel && nextThreshold && (
            <div className="emp-next-threshold">
              {nextThreshold.pointsAway} pts to {nextThreshold.label}
            </div>
          )}
          {isSelected ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      {isSelected && (
        <div className="emp-point-details">
          {/* Progress bar toward thresholds */}
          <div className="emp-progress-section">
            <div className="emp-progress-bar-container">
              <div className="emp-progress-bar">
                <div
                  className="emp-progress-fill"
                  style={{
                    width: `${Math.min((totalPoints / 8) * 100, 100)}%`,
                    background: totalPoints >= 8 ? '#991b1b'
                      : totalPoints >= 5 ? '#ef4444'
                      : totalPoints >= 3 ? '#f59e0b'
                      : '#10b981'
                  }}
                />
                {THRESHOLDS.map(t => (
                  <div
                    key={t.level}
                    className="emp-progress-marker"
                    style={{ left: `${(t.points / 8) * 100}%` }}
                    title={`${t.points} pts - ${t.label}`}
                  >
                    <span className="emp-progress-marker-line" />
                  </div>
                ))}
              </div>
              <div className="emp-progress-labels">
                <span>0</span>
                <span>3</span>
                <span>5</span>
                <span>7</span>
                <span>8</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="emp-point-actions">
            <button className="btn-add-point" onClick={onAddPoint}>
              <Plus size={14} /> Log Point
            </button>
            {totalPoints >= 3 && (
              <button
                className="btn-writeup"
                onClick={() => openWriteUpForPrint({
                  employee,
                  totalPoints,
                  warningLevel,
                  pointHistory: points,
                  managerName: '',
                  additionalNotes: '',
                })}
              >
                <FileText size={14} /> Generate Write-Up
              </button>
            )}
            {!employee.employment.ptoEligible && (
              <button
                className="btn-pto-alert"
                onClick={() => {
                  const subject = encodeURIComponent(`PTO Eligibility Change - ${employee.firstName} ${employee.lastName}`);
                  const body = encodeURIComponent(
                    `Hi,\n\nThis is to notify you that ${employee.firstName} ${employee.lastName} has dropped below the full-time threshold of ${employee.employment.threshold} hours/week.\n\nCurrent average: ${employee.employment.avgWeeklyHours} hrs/week (8-week average)\nStatus: Part-Time\nPTO Eligible: No\n\nPlease update their benefits status accordingly.\n\nThank you,\nTwo Sisters Operations System`
                  );
                  window.open(`mailto:HR@twosisterscleaningco.com?subject=${subject}&body=${body}`);
                }}
              >
                <Mail size={14} /> Notify HR
              </button>
            )}
          </div>

          {/* Point History */}
          <div className="emp-point-history">
            <h4>Point History <span className="history-count">({activePoints.length} active, 90-day window)</span></h4>
            {activePoints.length === 0 ? (
              <div className="no-points">No attendance points in the last 90 days. Great job!</div>
            ) : (
              <div className="point-list">
                {activePoints
                  .sort((a, b) => (b.date || b.createdAt).localeCompare(a.date || a.createdAt))
                  .map(p => (
                    <div key={p.id} className={`point-entry ${p.voided ? 'voided' : ''} ${p.points < 0 ? 'reduction' : ''}`}>
                      <div className="point-entry-icon" style={{ color: POINT_TYPES[p.type]?.color || '#6b7280' }}>
                        {typeIcon(p.type)}
                      </div>
                      <div className="point-entry-info">
                        <div className="point-entry-desc">
                          {p.description || POINT_TYPES[p.type]?.label || p.type}
                          {p.voided && <span className="voided-badge">VOIDED</span>}
                        </div>
                        <div className="point-entry-meta">
                          {p.date && format(new Date(p.date), 'MMM d, yyyy')}
                          {p.scheduledTime && ` | Scheduled: ${p.scheduledTime}`}
                          {p.actualTime && ` | Actual: ${format(new Date(p.actualTime), 'h:mm a')}`}
                        </div>
                        {p.managerNote && (
                          <div className="point-entry-note">
                            <Info size={12} /> {p.managerNote}
                          </div>
                        )}
                        {p.voidReason && (
                          <div className="point-entry-note void-note">
                            <Shield size={12} /> Void reason: {p.voidReason}
                          </div>
                        )}
                      </div>
                      <div className="point-entry-right">
                        <span className={`point-value ${p.points < 0 ? 'positive' : 'negative'}`}>
                          {formatPoints(p.points)}
                        </span>
                        <button className="btn-edit-point" onClick={() => onEditPoint(p)} title="Edit / Override">
                          <Edit3 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== ADD POINT MODAL =====
function AddPointModal({ employeeId, employees, onClose, onSave }) {
  const emp = employees.find(e => e.id === employeeId);
  const [type, setType] = useState('tardiness');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [points, setPoints] = useState('');
  const [description, setDescription] = useState('');
  const [managerNote, setManagerNote] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [actualTime, setActualTime] = useState('');
  const [calloutCategory, setCalloutCategory] = useState('under_24hr_unexcused');
  const [hasPTO, setHasPTO] = useState(false);

  // Auto-calculate points for tardiness/early leave
  const autoPoints = useMemo(() => {
    if (type === 'tardiness' && scheduledTime && actualTime) {
      const [sh, sm] = scheduledTime.split(':').map(Number);
      const [ah, am] = actualTime.split(':').map(Number);
      const diff = (ah * 60 + am) - (sh * 60 + sm);
      if (diff > 0) {
        const result = calculateTardinessPoints(diff);
        return result?.points || 0;
      }
    }
    if (type === 'early_leave' && scheduledTime && actualTime) {
      const [sh, sm] = scheduledTime.split(':').map(Number);
      const [ah, am] = actualTime.split(':').map(Number);
      const diff = (sh * 60 + sm) - (ah * 60 + am);
      if (diff > 0) {
        const result = calculateEarlyLeavePoints(diff);
        return result?.points || 0;
      }
    }
    if (type === 'callout') {
      const tier = CALLOUT_POINTS[calloutCategory];
      if (tier) return hasPTO ? tier.withPTO : tier.withoutPTO;
    }
    if (type === 'no_call_no_show') return 7;
    return '';
  }, [type, scheduledTime, actualTime, calloutCategory, hasPTO]);

  const finalPoints = points !== '' ? parseFloat(points) : (autoPoints !== '' ? autoPoints : 0);

  const handleSave = () => {
    onSave({
      employeeId,
      type,
      date,
      points: type === 'reduction' ? -Math.abs(finalPoints) : finalPoints,
      description: description || getAutoDescription(),
      managerNote,
      scheduledTime: scheduledTime || null,
      actualTime: actualTime || null,
      calloutCategory: type === 'callout' ? calloutCategory : null,
      hasPTO: type === 'callout' ? hasPTO : null,
    });
  };

  const getAutoDescription = () => {
    if (type === 'tardiness') return `Late to shift (${scheduledTime || 'N/A'})`;
    if (type === 'early_leave') return `Left shift early (scheduled end: ${scheduledTime || 'N/A'})`;
    if (type === 'callout') return CALLOUT_POINTS[calloutCategory]?.label || 'Call out';
    if (type === 'no_call_no_show') return 'No call / no show';
    if (type === 'reduction') return 'Perfect attendance reduction';
    if (type === 'manager_override') return 'Manager adjustment';
    return '';
  };

  return (
    <div className="att-modal-overlay" onClick={onClose}>
      <div className="att-modal" onClick={e => e.stopPropagation()}>
        <div className="att-modal-header">
          <h3>Log Attendance Point</h3>
          <button className="att-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="att-modal-body">
          <div className="att-modal-emp">
            <User size={16} />
            <strong>{emp?.firstName} {emp?.lastName}</strong>
          </div>

          <div className="att-form-row">
            <label>Type</label>
            <select value={type} onChange={e => setType(e.target.value)}>
              <option value="tardiness">Tardiness (Late Clock-In)</option>
              <option value="early_leave">Left Early</option>
              <option value="callout">Call Out</option>
              <option value="no_call_no_show">No Call / No Show</option>
              <option value="reduction">Point Reduction (-)</option>
              <option value="manager_override">Manager Adjustment</option>
            </select>
          </div>

          <div className="att-form-row">
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>

          {(type === 'tardiness' || type === 'early_leave') && (
            <>
              <div className="att-form-row-pair">
                <div className="att-form-row">
                  <label>{type === 'tardiness' ? 'Scheduled Start' : 'Scheduled End'}</label>
                  <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} />
                </div>
                <div className="att-form-row">
                  <label>{type === 'tardiness' ? 'Actual Clock-In' : 'Actual Clock-Out'}</label>
                  <input type="time" value={actualTime} onChange={e => setActualTime(e.target.value)} />
                </div>
              </div>
              {autoPoints !== '' && (
                <div className="att-auto-calc">
                  Auto-calculated: <strong>{autoPoints} points</strong>
                </div>
              )}
            </>
          )}

          {type === 'callout' && (
            <>
              <div className="att-form-row">
                <label>Notice Period</label>
                <select value={calloutCategory} onChange={e => setCalloutCategory(e.target.value)}>
                  {Object.entries(CALLOUT_POINTS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div className="att-form-row checkbox-row">
                <label>
                  <input type="checkbox" checked={hasPTO} onChange={e => setHasPTO(e.target.checked)} />
                  Used PTO
                </label>
              </div>
              {autoPoints !== '' && (
                <div className="att-auto-calc">
                  Auto-calculated: <strong>{autoPoints} points</strong> ({hasPTO ? 'with PTO' : 'no PTO'})
                </div>
              )}
            </>
          )}

          <div className="att-form-row">
            <label>Points {type === 'reduction' ? '(will be negative)' : ''}</label>
            <input
              type="number"
              step="0.25"
              value={points !== '' ? points : (autoPoints !== '' ? autoPoints : '')}
              onChange={e => setPoints(e.target.value)}
              placeholder={autoPoints !== '' ? `Auto: ${autoPoints}` : 'Enter points'}
            />
            <span className="att-form-hint">Override the auto-calculated value if needed</span>
          </div>

          <div className="att-form-row">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={getAutoDescription()}
            />
          </div>

          <div className="att-form-row">
            <label>Manager Note</label>
            <textarea
              value={managerNote}
              onChange={e => setManagerNote(e.target.value)}
              placeholder="Optional: Add context about what happened, why the points were adjusted, etc."
              rows={3}
            />
          </div>
        </div>

        <div className="att-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={handleSave} disabled={finalPoints === 0 && type !== 'reduction'}>
            <Check size={16} /> Log {type === 'reduction' ? 'Reduction' : 'Point'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== EDIT POINT MODAL =====
function EditPointModal({ point, onClose, onSave, onVoid }) {
  const [editPoints, setEditPoints] = useState(point.points);
  const [editDesc, setEditDesc] = useState(point.description || '');
  const [managerNote, setManagerNote] = useState(point.managerNote || '');
  const [voidReason, setVoidReason] = useState('');
  const [showVoid, setShowVoid] = useState(false);

  return (
    <div className="att-modal-overlay" onClick={onClose}>
      <div className="att-modal" onClick={e => e.stopPropagation()}>
        <div className="att-modal-header">
          <h3>{showVoid ? 'Void Point' : 'Edit / Override Point'}</h3>
          <button className="att-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="att-modal-body">
          <div className="att-edit-summary">
            <div><strong>Type:</strong> {POINT_TYPES[point.type]?.label || point.type}</div>
            <div><strong>Date:</strong> {point.date && format(new Date(point.date), 'MMM d, yyyy')}</div>
            <div><strong>Original Points:</strong> {point.points}</div>
            {point.description && <div><strong>Description:</strong> {point.description}</div>}
          </div>

          {!showVoid ? (
            <>
              <div className="att-form-row">
                <label>Adjusted Points</label>
                <input
                  type="number"
                  step="0.25"
                  value={editPoints}
                  onChange={e => setEditPoints(parseFloat(e.target.value) || 0)}
                />
                <span className="att-form-hint">Change the point value (e.g., reduce to 0 if excused)</span>
              </div>

              <div className="att-form-row">
                <label>Description</label>
                <input type="text" value={editDesc} onChange={e => setEditDesc(e.target.value)} />
              </div>

              <div className="att-form-row">
                <label>Manager Note (required for overrides)</label>
                <textarea
                  value={managerNote}
                  onChange={e => setManagerNote(e.target.value)}
                  placeholder="Explain why this point was adjusted..."
                  rows={3}
                />
              </div>

              <button className="btn-void-toggle" onClick={() => setShowVoid(true)}>
                <AlertTriangle size={14} /> Void this point entirely instead
              </button>
            </>
          ) : (
            <>
              <div className="att-form-row">
                <label>Reason for voiding (required)</label>
                <textarea
                  value={voidReason}
                  onChange={e => setVoidReason(e.target.value)}
                  placeholder="Explain why this point is being voided..."
                  rows={3}
                />
              </div>
              <button className="btn-void-toggle back" onClick={() => setShowVoid(false)}>
                Back to editing
              </button>
            </>
          )}
        </div>

        <div className="att-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          {showVoid ? (
            <button
              className="btn-void"
              onClick={() => onVoid(point.id, voidReason)}
              disabled={!voidReason.trim()}
            >
              <AlertTriangle size={16} /> Void Point
            </button>
          ) : (
            <button
              className="btn-save"
              onClick={() => onSave(point.id, {
                points: editPoints,
                description: editDesc,
                managerNote,
                overriddenAt: new Date().toISOString(),
              })}
              disabled={!managerNote.trim() && editPoints !== point.points}
            >
              <Check size={16} /> Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== SCAN MODAL — auto-detect points from time records vs shifts =====
function ScanModal({ employees, state, getEmployeeShifts, existingPoints, onClose, onAddPoints }) {
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedResults, setSelectedResults] = useState([]);
  const [daysBack, setDaysBack] = useState(7);
  const [hasScanned, setHasScanned] = useState(false);
  const [scanError, setScanError] = useState(null);

  const handleScan = async () => {
    setScanning(true);
    setScanError(null);

    try {
      const found = [];
      const now = new Date();
      const cutoff = format(subDays(now, daysBack), 'yyyy-MM-dd');
      const todayStr = format(now, 'yyyy-MM-dd');

      // Fetch time records from Firestore for the date range
      const recordsRef = collection(db, 'timeRecords');
      const q = query(
        recordsRef,
        where('date', '>=', cutoff),
        where('date', '<=', todayStr)
      );
      const snapshot = await getDocs(q);
      const timeRecords = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // For each employee, check time records vs shifts
      employees.forEach(emp => {
        const shifts = (getEmployeeShifts ? getEmployeeShifts(emp.id) : state.shifts.filter(s => s.employeeId === emp.id))
          .filter(s => s.date >= cutoff && s.date <= todayStr);

        shifts.forEach(shift => {
          // Find matching time records for this employee on this date
          const empRecords = timeRecords.filter(r =>
            r.employeeId === emp.id &&
            r.date === shift.date &&
            r.clockInTime
          );

          if (empRecords.length === 0) return;

          // Use the earliest clock-in and latest clock-out across records
          let earliestClockIn = null;
          let latestClockOut = null;
          empRecords.forEach(r => {
            if (r.clockInTime && (!earliestClockIn || r.clockInTime < earliestClockIn)) {
              earliestClockIn = r.clockInTime;
            }
            if (r.clockOutTime && (!latestClockOut || r.clockOutTime > latestClockOut)) {
              latestClockOut = r.clockOutTime;
            }
          });

          const timeRecord = {
            clockInTime: earliestClockIn,
            clockOutTime: latestClockOut,
          };

          const detected = autoDetectPoints(timeRecord, shift);

          detected.forEach(d => {
            // Check if this point was already logged
            const alreadyLogged = existingPoints.some(ep =>
              ep.employeeId === emp.id &&
              ep.date === shift.date &&
              ep.type === d.type &&
              !ep.voided
            );
            if (!alreadyLogged) {
              found.push({
                ...d,
                employeeId: emp.id,
                employeeName: `${emp.firstName} ${emp.lastName}`,
                date: shift.date,
              });
            }
          });
        });
      });

      setResults(found);
      setSelectedResults(found.map((_, i) => i));
      setHasScanned(true);
    } catch (err) {
      console.error('Scan failed:', err);
      setScanError('Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const toggleResult = (idx) => {
    setSelectedResults(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const handleApply = () => {
    const toAdd = selectedResults.map(i => results[i]);
    onAddPoints(toAdd);
  };

  return (
    <div className="att-modal-overlay" onClick={onClose}>
      <div className="att-modal scan-modal" onClick={e => e.stopPropagation()}>
        <div className="att-modal-header">
          <h3>Scan Time Records for Attendance Issues</h3>
          <button className="att-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="att-modal-body">
          <p className="scan-desc">
            Compare employee clock-in times against their scheduled shifts to automatically detect tardiness. Early departures must be logged manually by a manager.
          </p>
          <div className="att-form-row-pair">
            <div className="att-form-row">
              <label>Look back</label>
              <select value={daysBack} onChange={e => setDaysBack(Number(e.target.value))}>
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
            <div className="att-form-row" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn-save" onClick={handleScan} disabled={scanning}>
                <Search size={16} /> {scanning ? 'Scanning...' : hasScanned ? 'Rescan' : 'Run Scan'}
              </button>
            </div>
          </div>

          {results.length > 0 && (
            <>
              <h4 className="scan-results-title">Found {results.length} unlogged issue{results.length !== 1 ? 's' : ''}</h4>
              <div className="scan-results-list">
                {results.map((r, idx) => (
                  <label key={idx} className={`scan-result-item ${selectedResults.includes(idx) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedResults.includes(idx)}
                      onChange={() => toggleResult(idx)}
                    />
                    <div className="scan-result-info">
                      <strong>{r.employeeName}</strong>
                      <span>{r.description} on {format(new Date(r.date + 'T12:00:00'), 'MMM d')}</span>
                    </div>
                    <span className="scan-result-pts">+{r.points}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          {scanError && (
            <div className="scan-empty" style={{ color: '#dc2626' }}>{scanError}</div>
          )}
          {results.length === 0 && !scanning && !scanError && (
            <div className="scan-empty">
              {hasScanned
                ? 'No attendance issues found for the selected time period. Change the lookback period and rescan if needed.'
                : 'Click "Run Scan" to check for attendance issues.'}
            </div>
          )}
        </div>
        <div className="att-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
          {results.length > 0 && (
            <button className="btn-save" onClick={handleApply} disabled={selectedResults.length === 0}>
              <Check size={16} /> Log {selectedResults.length} Point{selectedResults.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttendancePoints;
